import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { Package, Plus, FileDown, MapPin, TrendingUp, ChevronDown, ChevronUp, Eye, Pencil, Trash2, Download, Mail, Printer } from "lucide-react";
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from "../common/index.js";

const formatLocalDate = (date = new Date()) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const ShipmentList = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showStatsPanel, setShowStatsPanel] = useState(false)
  const [filters, setFilters] = useState(() => {
    const todayStr = formatLocalDate()
    return {
      status: "",
      search: "",
      from_date: todayStr,
      to_date: todayStr,
      mode_of_payment: [],
      user_id: "",
      sender_location: [],
      receiver_location: [],
    }
  })
  const [users, setUsers] = useState([])
  const [locationHubs, setLocationHubs] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  const [deleteModalShipment, setDeleteModalShipment] = useState(null)
  const [deleteReason, setDeleteReason] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [reasonModalShipment, setReasonModalShipment] = useState(null)
  const [locationStats, setLocationStats] = useState([])
  const [statsSummary, setStatsSummary] = useState({ total_booked_shipments: 0, total_charges: 0, total_paid: 0, total_balance: 0, payment_modes: {} })
  const [selectedStatLocation, setSelectedStatLocation] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)

  useEffect(() => {
    fetchShipments()
    fetchUsers()
    fetchLocationHubs()
    fetchLocationStats()
  }, [filters, currentPage])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId])

    const fetchShipments = async () => {
    try {
      setLoading(true)
      const cleanFilters = Object.fromEntries(
        Object.entries(filters)
          .filter(([_, value]) => {
            if (Array.isArray(value)) return value.length > 0
            return value !== "" && value !== null
          })
          .map(([key, value]) => [key, Array.isArray(value) ? value.join(",") : value])
      )

      const response = await shipmentService.getShipments({
        ...cleanFilters,
        page: currentPage,
        per_page: 20,
      })

      setShipments(response.data.shipments || [])
      setTotalPages(response.data.pagination?.pages || 1)
    } catch (error) {
      console.error("Error fetching shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to load shipments")
      setShipments([])
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await shipmentService.getDistinctUsers()
      setUsers(response.data.users || [])
    } catch (error) {
      console.error("Error fetching users:", error)
    }
  }

  const fetchLocationHubs = async () => {
    try {
      const response = await shipmentService.getAllLocationHubs()
      setLocationHubs(response.data.hubs || response.data || [])
    } catch (error) {
      console.error("Error fetching location hubs:", error)
    }
  }

  const fetchLocationStats = async () => {
    try {
      setStatsLoading(true)
      const dateFilters = {}
      if (filters.from_date) dateFilters.from_date = filters.from_date
      if (filters.to_date) dateFilters.to_date = filters.to_date
      const response = await shipmentService.getBookedShipmentsBySenderLocation(dateFilters)
      setLocationStats(response.data.data || [])
      setStatsSummary(response.data.summary || { total_booked_shipments: 0, total_charges: 0 })
    } catch (error) {
      console.error("Error fetching location stats:", error)
    } finally {
      setStatsLoading(false)
    }
  }

  const handleClearFilters = () => {
    const todayStr = formatLocalDate()

    setFilters({
      status: "",
      search: "",
      from_date: todayStr,
      to_date: todayStr,
      mode_of_payment: [],
      user_id: "",
      sender_location: [],
      receiver_location: [],
    })
    setCurrentPage(1)
  }

  const getStatusColor = (status) => {
    const colors = {
      Booked: "bg-blue-100 text-blue-800 border-blue-200",
      Dispatched: "bg-purple-100 text-purple-800 border-purple-200",
      Received: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Delivered: "bg-green-100 text-green-800 border-green-200",
      Returned: "bg-red-100 text-red-800 border-red-200",
      Rejected: "bg-gray-100 text-gray-800 border-gray-200",
    }
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  const handleExportPDF = async () => {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters)
          .filter(([_, value]) => (Array.isArray(value) ? value.length > 0 : value !== ""))
          .map(([key, value]) => [key, Array.isArray(value) ? value.join(",") : value])
      )
      const response = await shipmentService.exportPDF(cleanFilters)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `shipments_${formatLocalDate()}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("PDF exported successfully")
    } catch (error) {
      Alert.error("Failed to export PDF")
    }
  }

  const toggleMenu = (shipmentId) => {
    setOpenMenuId(openMenuId === shipmentId ? null : shipmentId)
  }

  const handleDownloadInvoice = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.downloadInvoice(shipment.shipment_id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `invoice_${shipment.shipment_id}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("Invoice downloaded successfully")
    } catch (error) {
      Alert.error("Failed to download invoice")
    }
  }

  const handleSendInvoice = async (shipment) => {
    try {
      setOpenMenuId(null)
      const email = shipment.consigner_email || shipment.sender_email || "sender"
      const confirmed = await Alert.confirm(`Send invoice to ${email}?`, "Send Invoice")
      if (!confirmed) return

      await shipmentService.sendInvoiceEmail(shipment.shipment_id)
      Alert.success("Invoice sent successfully")
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to send invoice")
    }
  }

  const handlePrintReceipt = async (shipment) => {
    try {
      const response = await shipmentService.printReceipt(shipment.shipment_id)
      
      // The API returns HTML directly, not JSON
      const printWindow = window.open('', '_blank', 'width=400,height=600')
      printWindow.document.write(response.data)
      printWindow.document.close()
      
      // Auto-trigger print dialog after a brief delay
      setTimeout(() => {
        printWindow.print()
      }, 250)
      
      Alert.success("Receipt opened for printing")
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to print receipt")
    }
  }

  const handleExportPreLoadingSheet = async () => {
    try {
      const preLoadingFilters = {}
      if (filters.sender_location?.length > 0) preLoadingFilters.sender_location = filters.sender_location.join(",")
      if (filters.receiver_location?.length > 0) preLoadingFilters.receiver_location = filters.receiver_location.join(",")
      if (filters.mode_of_payment?.length > 0) preLoadingFilters.mode_of_payment = filters.mode_of_payment.join(",")
      const response = await shipmentService.exportPreLoadingSheet(preLoadingFilters)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `pre_loading_sheet_upto_${formatLocalDate()}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("Pre-loading sheet downloaded successfully")
    } catch (error) {
      Alert.error("Failed to download pre-loading sheet")
    }
  }

  const openDeleteModal = (shipment) => {
    setOpenMenuId(null)
    setDeleteReason("")
    setDeleteModalShipment(shipment)
  }

  const closeDeleteModal = () => {
    if (deleting) return
    setDeleteModalShipment(null)
    setDeleteReason("")
  }

  const handleDeleteShipment = async () => {
    const shipment = deleteModalShipment
    const reason = deleteReason.trim()
    if (!shipment) return
    if (!reason) {
      Alert.error("Please enter a reason before deleting")
      return
    }

    try {
      setDeleting(true)
      await shipmentService.deleteShipment(shipment.shipment_id, reason)
      Alert.success("Shipment marked as deleted")
      setDeleteModalShipment(null)
      setDeleteReason("")
      fetchShipments()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to delete shipment")
    } finally {
      setDeleting(false)
    }
  }

  const handleShipmentClick = (shipment) => {
    if (shipment.is_deleted) {
      setReasonModalShipment(shipment)
      return
    }
    navigate(`/shipments/view/${shipment.shipment_id}`)
  }

  const columns = [
    {
      key: 'shipment_id',
      label: 'Shipment ID',
      render: (shipment) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleShipmentClick(shipment)
          }}
          className={`font-medium ${shipment.is_deleted ? "text-gray-500" : "text-brand hover:underline"}`}
        >
          {shipment.shipment_id}
        </button>
      ),
    },
    {
      key: 'consigner',
      label: 'Consigner',
      render: (shipment) => (
        <div>
          <div>{shipment.consigner_name || shipment.sender_name}</div>
          <div className="text-xs text-gray-500">{shipment.consigner_contact || shipment.sender_contact}</div>
          {shipment.sender_location && (
            <div className="text-xs text-gray-500">📍 {shipment.sender_location}</div>
          )}
        </div>
      ),
    },
    {
      key: 'consignee',
      label: 'Consignee',
      render: (shipment) => (
        <div>
          <div>{shipment.consignee_name || shipment.receiver_name}</div>
          <div className="text-xs text-gray-500">{shipment.consignee_contact || shipment.receiver_contact}</div>
          {shipment.receiver_location && (
            <div className="text-xs text-gray-500">📍 {shipment.receiver_location}</div>
          )}
        </div>
      ),
    },
    {
      key: 'no_of_articles',
      label: 'Articles',
      render: (shipment) => shipment.no_of_articles ?? 1,
    },
    {
      key: 'mode_of_payment',
      label: 'Payment Mode',
      render: (shipment) =>
        shipment.mode_of_payment ? shipment.mode_of_payment.replace(/_/g, ' ') : 'N/A',
    },
    {
      key: 'status',
      label: 'Status',
      render: (shipment) => (
        <StatusBadge
          status={shipment.is_deleted ? "Deleted" : shipment.status}
        />
      ),
    },
    {
      key: 'total_amount',
      label: 'Amount',
      render: (shipment) => `₹${shipment.total_amount?.toFixed(2)}`,
    },
    {
      key: 'balance_amount',
      label: 'Balance',
      render: (shipment) => `₹${shipment.balance_amount?.toFixed(2)}`,
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (shipment) =>
        new Date(shipment.created_at).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
  ]

  const getActions = (shipment) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => handleShipmentClick(shipment),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/shipments/edit/${shipment.shipment_id}`),
      visible: user?.role !== 'Accountant' && !shipment.is_deleted,
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => openDeleteModal(shipment),
      visible: ['System Admin', 'Admin', 'Accountant'].includes(user?.role) && !shipment.is_deleted,
    },
    {
      label: 'Download Invoice',
      icon: Download,
      onClick: () => handleDownloadInvoice(shipment),
      visible: !shipment.is_deleted,
    },
    {
      label: 'Send Invoice',
      icon: Mail,
      onClick: () => handleSendInvoice(shipment),
      visible: !shipment.is_deleted,
    },
    {
      label: 'Print Receipt',
      icon: Printer,
      onClick: () => handlePrintReceipt(shipment),
      visible: !shipment.is_deleted,
    },
  ]

  return (
    <PageLayout
      title="Shipments"
      icon={Package}
      maxWidth="max-w-full"
      actions={
        <>
          {user?.role !== 'Driver' && (
            <Button icon={Plus} onClick={() => navigate('/shipments/add')}>
              Add Shipment
            </Button>
          )}
          {['System Admin', 'Admin', 'Accountant'].includes(user?.role) && (
            <>
              <Button variant="outline" icon={FileDown} onClick={handleExportPDF}>
                Export PDF
              </Button>
              <Button variant="outline" icon={FileDown} onClick={handleExportPreLoadingSheet}>
                Pre-Loading Sheet
              </Button>
            </>
          )}
        </>
      }
    >
                {/* Location Stats Panel */}
        <div className="bg-white rounded-card shadow-card mb-4 border border-gray-100 overflow-hidden">
                  {/* Toggle Header */}
                  <button
                    onClick={() => setShowStatsPanel(!showStatsPanel)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-semibold text-gray-700">Booked Shipments Analytics</span>
                      <div className="flex items-center gap-2 ml-2 flex-wrap">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                          {statsSummary.total_booked_shipments} Booked
                        </span>
                        <span className="text-xs bg-blue-100 text-brand px-2 py-0.5 rounded-full font-semibold">
                          ₹{Number(statsSummary.total_charges || 0).toLocaleString("en-IN")} Total
                        </span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                          ₹{Number(statsSummary.total_paid || 0).toLocaleString("en-IN")} Paid
                        </span>
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">
                          ₹{Number(statsSummary.total_balance || 0).toLocaleString("en-IN")} Due
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      {showStatsPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>

                  {/* Expandable Stats Content */}
                  {showStatsPanel && (
                    <div className="border-t border-gray-100 px-4 py-4">
                      {statsLoading ? (
                        <div className="flex justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-brand"></div>
                        </div>
                      ) : locationStats.length === 0 ? (
                        <p className="text-center text-gray-500 text-sm py-4">No booked shipments data available</p>
                      ) : (
                        <div>
                          {/* Payment Mode Summary Row */}
                          {Object.keys(statsSummary.payment_modes || {}).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-100">
                              {Object.entries(statsSummary.payment_modes).map(([mode, count]) => {
                                const modeColors = {
                                  to_pay: 'bg-yellow-50 text-yellow-700 border-yellow-200',
                                  paid: 'bg-green-50 text-green-700 border-green-200',
                                  credit: 'bg-purple-50 text-purple-700 border-purple-200',
                                  paid_balance: 'bg-brand-light text-brand border-blue-200',
                                  foc: 'bg-gray-50 text-gray-600 border-gray-200',
                                }
                                const colorClass = modeColors[mode] || 'bg-gray-50 text-gray-600 border-gray-200'
                                return (
                                  <span key={mode} className={`text-xs px-2.5 py-1 rounded-sm border font-semibold ${colorClass}`}>
                                    {mode.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: {count}
                                  </span>
                                )
                              })}
                            </div>
                          )}

                      {/* Location Cards Grid - Compact */}
                          <div className="flex flex-wrap gap-2">
                            {locationStats.map((loc) => (
                              <button
                                key={loc.sender_location}
                                onClick={() => setSelectedStatLocation(selectedStatLocation === loc.sender_location ? null : loc.sender_location)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs transition-all ${
                                  selectedStatLocation === loc.sender_location
                                    ? "border-blue-400 bg-blue-50 text-blue-700"
                                    : "border-gray-200 bg-gray-50 text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                                }`}
                              >
                                <MapPin className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                <span className="font-semibold truncate max-w-[80px]">{loc.sender_location}</span>
                                <span className="font-bold text-blue-600">{loc.total_shipments}</span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-500">₹{Number(loc.total_charges).toLocaleString("en-IN")}</span>
                              </button>
                            ))}
                          </div>

                          {/* Selected Location Detail - Compact Bar */}
                          {selectedStatLocation && (() => {
                            const loc = locationStats.find(l => l.sender_location === selectedStatLocation)
                            if (!loc) return null
                            const maxShipments = Math.max(...locationStats.map(l => l.total_shipments))
                            const pct = Math.round((loc.total_shipments / maxShipments) * 100)
                            return (
                              <div className="mt-2 flex items-center gap-3 p-2 bg-blue-50 border border-blue-200 rounded-sm">
                                <div className="flex items-center gap-1 min-w-[80px]">
                                  <MapPin className="w-3 h-3 text-blue-500" />
                                  <span className="text-xs font-bold text-gray-800 truncate">{loc.sender_location}</span>
                                </div>
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                                </div>
                                <div className="flex gap-3 text-xs flex-wrap">
                                  <span className="font-semibold text-blue-700 whitespace-nowrap">{loc.total_shipments} shipments</span>
                                  <span className="font-semibold text-brand whitespace-nowrap">₹{Number(loc.total_charges).toLocaleString("en-IN")} total</span>
                                  <span className="font-semibold text-green-700 whitespace-nowrap">₹{Number(loc.total_paid || 0).toLocaleString("en-IN")} paid</span>
                                  <span className="font-semibold text-red-600 whitespace-nowrap">₹{Number(loc.total_balance || 0).toLocaleString("en-IN")} due</span>
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Filters Section */}
                <FilterPanel
                  clearLabel="Clear All"
                  filters={[
                    { key: 'search', label: 'Search', placeholder: 'ID, consigner, consignee...' },
                    {
                      key: 'status',
                      label: 'Status',
                      type: 'select',
                      options: [
                        { value: 'Booked', label: 'Booked' },
                        { value: 'Dispatched', label: 'Dispatched' },
                        { value: 'Received', label: 'Received' },
                        { value: 'Delivered', label: 'Delivered' },
                        { value: 'Returned', label: 'Returned' },
                        { value: 'Rejected', label: 'Rejected' },
                      ],
                    },
                    {
                      key: 'mode_of_payment',
                      label: 'Payment Mode',
                      type: 'multiselect',
                      placeholder: 'All Payment Modes',
                      options: [
                        { value: 'to_pay', label: 'To Pay' },
                        { value: 'credit', label: 'Credit' },
                        { value: 'paid', label: 'Paid' },
                        { value: 'paid_balance', label: 'Paid Balance' },
                      ],
                    },
                    {
                      key: 'user_id',
                      label: 'User',
                      type: 'select',
                      visible: ['System Admin', 'Admin', 'Accountant'].includes(user?.role),
                      options: users.map((u) => ({ value: u.user_id, label: u.name })),
                    },
                    { key: 'from_date', label: 'From Date', type: 'date' },
                    { key: 'to_date', label: 'To Date', type: 'date' },
                    {
                      key: 'sender_location',
                      label: 'Sender Location',
                      type: 'multiselect',
                      placeholder: 'All Sender Locations',
                      options: locationHubs.map((hub) => ({ value: hub.location, label: hub.location })),
                    },
                    {
                      key: 'receiver_location',
                      label: 'Receiver Location',
                      type: 'multiselect',
                      placeholder: 'All Receiver Locations',
                      options: locationHubs.map((hub) => ({ value: hub.location, label: hub.location })),
                    },
                  ]}
                  values={filters}
                  onChange={(vals) => {
                    setFilters(vals)
                    setCurrentPage(1)
                  }}
                  onClear={handleClearFilters}
                />

                {/* Shipments Table */}
                <DataTable
                  columns={columns}
                  data={shipments}
                  loading={loading}
                  emptyMessage="No shipments found"
                  rowKey="shipment_id"
                  actions={getActions}
                  onRowClick={handleShipmentClick}
                  getRowClassName={(shipment) =>
                    shipment.is_deleted ? "line-through text-gray-400 bg-gray-50 hover:bg-gray-100" : ""
                  }
                />

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />

                {deleteModalShipment && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
                      <h3 className="text-lg font-semibold text-gray-900">Delete Shipment</h3>
                      <p className="text-sm text-gray-600 mt-2">
                        Shipment <span className="font-medium">{deleteModalShipment.shipment_id}</span> will stay in this list with a strikethrough. Enter a reason to continue.
                      </p>
                      <label className="block text-xs font-medium text-gray-700 mt-4 mb-1">
                        Reason <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        rows={4}
                        placeholder="Enter delete reason"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                      />
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          type="button"
                          onClick={closeDeleteModal}
                          disabled={deleting}
                          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteShipment}
                          disabled={deleting || !deleteReason.trim()}
                          className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {deleting ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {reasonModalShipment && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setReasonModalShipment(null)}
                  >
                    <div
                      className="bg-white rounded-lg shadow-xl w-full max-w-md p-5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h3 className="text-lg font-semibold text-gray-900">Deleted Shipment</h3>
                      <p className="text-sm text-gray-500 mt-1">{reasonModalShipment.shipment_id}</p>
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-500 uppercase">Reason</p>
                        <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">
                          {reasonModalShipment.delete_reason || "No reason recorded"}
                        </p>
                      </div>
                      {reasonModalShipment.deleted_at && (
                        <p className="text-xs text-gray-500 mt-3">
                          Deleted on {new Date(reasonModalShipment.deleted_at).toLocaleString("en-IN")}
                        </p>
                      )}
                      <div className="flex justify-end mt-4">
                        <button
                          type="button"
                          onClick={() => setReasonModalShipment(null)}
                          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
    </PageLayout>
  )
}

export default ShipmentList;